<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useQuizterStore } from '../../stores/quizterStore';
import Card from '../../components/Card.vue';
import NextButton from '../../components/NextButton.vue';
import useLocale from '../../../composables/use-locale';

const emit = defineEmits<{
  proceed: [];
}>();

const { t } = useLocale();

const quizterStore = useQuizterStore();
const { gameOptions } = storeToRefs(quizterStore);
const { setGameOptions } = quizterStore;

// Time selection
const timePreset = ref<number>(20000);
const customTime = ref<number>(20);
const showCustomTime = ref(false);

const timeButtons = [
  {
    label: t('settings.time_limit_seconds', { seconds: 20 }),
    value: 20000, // Store in milliseconds
  },
  {
    label: t('settings.time_limit_seconds', { seconds: 40 }),
    value: 40000, // Store in milliseconds
  },
  {
    label: t('settings.time_limit_custom'),
    value: -1,
  },
];

watch(timePreset, (val) => {
  if (val !== -1) {
    gameOptions.value.questionAnswerTimeout = val;
    showCustomTime.value = false;
  } else {
    showCustomTime.value = true;
    // Convert seconds to milliseconds
    gameOptions.value.questionAnswerTimeout = customTime.value * 1000;
  }
});

const handleCustomTimeBlur = () => {
  const clampedValue = Math.min(Math.max(customTime.value, 10), 120);
  customTime.value = clampedValue;
  // Convert seconds to milliseconds when storing
  gameOptions.value.questionAnswerTimeout = clampedValue * 1000;
};

const handleProceed = () => {
  // Ensure validation if custom time is active
  if (showCustomTime.value) {
    handleCustomTimeBlur();
  }
  if (!gameOptions.value.useQuestionShowTimer) {
    gameOptions.value.questionShowTimeout = 0;
  }
  setGameOptions(gameOptions.value);
  emit('proceed');
};

const headingRef = ref<HTMLElement | null>(null);
onMounted(() => {
  if (headingRef.value) {
    headingRef.value.focus();
  }
});

const infoIconQuestionTimer = ref();
const infoIconAnswerRecap = ref();
</script>

<template>
  <Card :content-max-width="500">
    <div class="d-flex flex-column gr-8">
      <h1 ref="headingRef" class="chalkboard-font text-center my-0 text-h2" tabindex="-1">
        {{ t('settings.title') }}
      </h1>

      <div>
        <div class="d-flex align-center justify-space-between">
          <span class="flex-grow-1">
            {{ t('settings.play_with_scoring') }}
          </span>
          <v-switch
            color="primary"
            v-model="gameOptions.useScoring"
            inset
            hide-details
            true-icon="fa:fawsb fa-check"
            :aria-label="t('settings.play_with_scoring')"
            data-testid="quizter-settings-use-scoring-switch"
          ></v-switch>
          <span class="ml-4" style="min-width: 40px">
            {{ gameOptions.useScoring ? t('on') : t('off') }}
          </span>
        </div>

        <div class="d-flex align-center justify-space-between">
          <span class="text-body-1 flex-grow-1">
            {{ t('settings.play_with_time_limit') }}
          </span>
          <v-switch
            color="primary"
            v-model="gameOptions.useTimer"
            inset
            hide-details
            true-icon="fa:fawsb fa-check"
            :aria-label="t('settings.play_with_time_limit')"
          ></v-switch>
          <span class="ml-4" style="min-width: 40px">
            {{ gameOptions.useTimer ? t('on') : t('off') }}
          </span>
        </div>

        <div v-if="gameOptions.useTimer" class="d-flex flex-column ga-4 mt-2 mb-4">
          <v-btn-toggle
            v-model="timePreset"
            mandatory
            variant="outlined"
            divided
            class="w-100 time-picker-toggle"
            base-color="on-surface"
          >
            <template v-for="btn in timeButtons" :key="btn.value">
              <v-btn :value="btn.value" class="flex-grow-1">
                {{ btn.label }}
              </v-btn>
            </template>
          </v-btn-toggle>

          <v-text-field
            class="d-flex flex-column align-self-center align-self-sm-baseline text-center"
            v-if="showCustomTime"
            v-model.number="customTime"
            type="number"
            :label="t('seconds')"
            min="10"
            max="120"
            variant="outlined"
            :hint="t('settings.hint_time_limit_custom')"
            persistent-hint
            style="max-width: 200px"
            @blur="handleCustomTimeBlur"
          ></v-text-field>
        </div>

        <div class="d-flex align-center justify-space-between">
          <span class="text-body-1 flex-grow-1">
            {{ t('settings.play_with_question_show_timer') }}
            <v-icon ref="infoIconQuestionTimer" icon="fa:fawsb fa-circle-info" class="ml-2"></v-icon>
            <v-tooltip location="right" max-width="150" :activator="infoIconQuestionTimer" :aria-hidden="true">
              {{ t('settings.question_show_timer_tooltip') }}
            </v-tooltip>
          </span>
          <v-switch
            color="primary"
            v-model="gameOptions.useQuestionShowTimer"
            inset
            hide-details
            true-icon="fa:fawsb fa-check"
            :aria-label="t('settings.play_with_question_show_timer')"
          ></v-switch>
          <span class="ml-4" style="min-width: 40px">
            {{ gameOptions.useQuestionShowTimer ? t('on') : t('off') }}
          </span>
        </div>

        <div class="d-flex align-center justify-space-between">
          <span class="text-body-1 flex-grow-1">
            {{ t('settings.show_answer_recap') }}
            <v-icon ref="infoIconAnswerRecap" icon="fa:fawsb fa-circle-info" class="ml-2"></v-icon>
            <v-tooltip location="right" max-width="220" :activator="infoIconAnswerRecap" :aria-hidden="true">
              {{ t('settings.show_answer_recap_tooltip') }}
            </v-tooltip>
          </span>
          <v-switch
            color="primary"
            v-model="gameOptions.showAnswerRecap"
            inset
            hide-details
            true-icon="fa:fawsb fa-check"
            :aria-label="t('settings.show_answer_recap')"
            data-testid="quizter-settings-show-answer-recap-switch"
          ></v-switch>
          <span class="ml-4" style="min-width: 40px">
            {{ gameOptions.showAnswerRecap ? t('on') : t('off') }}
          </span>
        </div>
      </div>

      <NextButton
        size="large"
        class="mt-8 align-self-center"
        @click="handleProceed"
        data-testid="quizter-settings-proceed-button"
      >
        {{ t('settings.proceed_to_game') }}
      </NextButton>
    </div>
  </Card>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

:deep(.v-selection-control--focused .v-switch__track) {
  outline: 2px solid rgb(var(--v-theme-outline));
  outline-offset: 1px;
}

:deep(.v-selection-control:not(.v-selection-control--dirty) .v-switch__thumb) {
  background-color: rgb(var(--v-theme-outline));
}

:deep(.v-btn-toggle) {
  .v-btn--active {
    background-color: transparent !important;
    border: 2px solid rgb(var(--v-theme-outline)) !important;

    .v-btn__overlay {
      opacity: 0 !important;
    }

    .v-btn__underlay {
      display: none;
    }
  }

  .v-btn {
    border: 1px solid rgba(var(--v-theme-outline), 0.5);
  }
}

:deep(.v-switch .v-switch__track) {
  background-color: rgb(var(--v-theme-surface-container-highest));
}

[tabindex='-1']:focus {
  outline: none;
}

// Mobile-specific styling
@media #{map.get(v.$display-breakpoints, 'xs')} {
  .v-btn-group {
    border-radius: 0 !important;
  }

  :deep(.time-picker-toggle) {
    flex-direction: column !important;
    height: auto !important;

    .v-btn {
      width: 100%;
      min-height: 70px;
      border-radius: 0 !important;
      text-transform: none;
      font-size: 1.125rem;
      letter-spacing: 0;

      .v-btn__content {
        font-family: 'chalkboard-font', sans-serif;
      }
    }

    .v-btn--active {
      border: 4px solid rgba(var(--v-theme-brand-original)) !important;
      z-index: 1;

      .v-btn__content {
        font-weight: 500;
      }
    }
  }
}
</style>
